import type { CoasterProject } from '../schema';

export const COASTERS_ASSERTION_AUDIENCE = 'buncha-coasters-api' as const;

export interface AnonymousViewer {
  kind: 'anonymous';
  id: null;
  displayName: 'Guest';
}

export interface AuthenticatedViewer {
  kind: 'authenticated';
  id: string;
  displayName: string;
  avatarUrl: string | null;
}

export type CoasterViewer = AnonymousViewer | AuthenticatedViewer;

export type ModerationStatus =
  | 'draft_private'
  | 'unlisted_read_only'
  | 'pending_review'
  | 'public'
  | 'rejected'
  | 'hidden'
  | 'archived';

export interface CloudDraftRecord {
  projectId: string;
  ownerId: string;
  version: number;
  project: CoasterProject;
  status: ModerationStatus;
  currentPublicRevisionId: string | null;
  publicCoasterId: string | null;
  publicSlug: string | null;
  updatedAt: string;
}

export interface CloudDraftSummary {
  projectId: string;
  title: string;
  version: number;
  status: ModerationStatus;
  publicCoasterId: string | null;
  publicSlug: string | null;
  updatedAt: string;
}

export interface SaveCloudDraftOptions {
  expectedVersion?: number | null;
}

export interface PublishRequest {
  projectId: string;
  expectedVersion: number;
  heroUpload?: HeroUploadDescriptor | null;
}

export interface PublishStatusRecord {
  projectId: string;
  status: ModerationStatus;
  pendingRevisionId: string | null;
  currentPublicRevisionId: string | null;
  publicCoasterId: string | null;
  publicSlug: string | null;
  rejectionReason: string | null;
  updatedAt: string;
}

export interface HeroUploadDescriptor {
  expiresAt: string;
  assets: {
    hero16x9: HeroAssetUploadDescriptor<1280, 720>;
    openGraph: HeroAssetUploadDescriptor<1200, 630>;
  };
}

export interface HeroAssetBundle {
  hero16x9: Blob;
  openGraph: Blob;
}

export interface HeroAssetUploadDescriptor<Width extends number, Height extends number> {
  uploadId: string;
  objectKey: string;
  uploadUrl: string;
  acceptedContentTypes: readonly ('image/webp' | 'image/png')[];
  maxBytes: number;
  requiredWidth: Width;
  requiredHeight: Height;
}

export interface PublicCoasterSummary {
  publicCoasterId: string;
  publicSlug: string;
  projectId: string;
  revisionId: string;
  title: string;
  description: string;
  creator: { id: string; displayName: string };
  themeId: string;
  heroUrl: string | null;
  allowRemix: boolean;
  publishedAt: string;
}

export interface PublicCoasterRecord extends PublicCoasterSummary {
  project: CoasterProject;
}

export interface ReadOnlyShare {
  shareId: string;
  secret: string;
  path: string;
  projectId: string;
  revisionId: string;
  createdAt: string;
}

export interface ReadOnlyShareRecord {
  shareId: string;
  projectId: string;
  revisionId: string;
  title: string;
  project: CoasterProject;
  allowRemix: boolean;
}

export interface RemixRequest {
  publicCoasterId?: string;
  share?: { shareId: string; secret: string };
}

export interface RemixResult {
  draft: CloudDraftRecord;
  lineage: NonNullable<CoasterProject['lineage']>;
}

export interface BunchaProfileResult {
  available: boolean;
  player?: { id: string; displayName: string; avatarUrl?: string | null };
  reason?: string;
}

export interface BunchaAssertionResult {
  available: boolean;
  token?: string;
  expiresAt?: string;
  reason?: string;
}

export interface BunchaPlayerApi {
  getProfile(): Promise<BunchaProfileResult>;
  getAssertion(request: {
    audience: typeof COASTERS_ASSERTION_AUDIENCE;
  }): Promise<BunchaAssertionResult>;
}

export interface BunchaWindowApi {
  player?: BunchaPlayerApi;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

declare global {
  interface Window {
    buncha?: BunchaWindowApi;
  }
}
